import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import {
  useCreateScheduledTaskMutation,
  useGetActiveScheduledTasksQuery,
  useGetScheduledTaskRegistryQuery,
  useGetScheduledTasksQuery,
  useGetSchedulerFailuresQuery,
  useUpdateScheduledTaskMutation,
} from 'src/redux/operations/operations.endpoints';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import {
  formatSchedulerMutationError,
  scheduleSortKey,
} from 'src/helpers/scheduler-schedule.helpers';
import type { ScheduledTask } from '../types/scheduler.types';
import { SchedulerStatusBadge } from '../components/scheduler-status-badge';
import { SchedulerTaskList } from '../components/scheduler-task-list';
import { SchedulerTaskSheet, type SchedulerTaskSheetMode } from '../components/scheduler-task-sheet';
import type { SchedulerTaskFormValues } from '../components/scheduler-task-form';

type SheetState =
  | { open: false }
  | { open: true; mode: SchedulerTaskSheetMode; task: ScheduledTask | null };

const SchedulerDashboardTab = () => {
  const [sheet, setSheet] = useState<SheetState>({ open: false });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: tasks, isLoading, isError, refetch, isFetching } = useGetScheduledTasksQuery();
  const { data: registry = [] } = useGetScheduledTaskRegistryQuery();
  const { data: activeTasks } = useGetActiveScheduledTasksQuery(undefined, {
    pollingInterval: 60_000,
  });
  const { data: failures } = useGetSchedulerFailuresQuery(undefined, {
    pollingInterval: 120_000,
  });
  const [createTask, { isLoading: isCreatingTask }] = useCreateScheduledTaskMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateScheduledTaskMutation();

  const sortedTasks = useMemo(
    () => [...(tasks ?? [])].sort((a, b) => scheduleSortKey(a).localeCompare(scheduleSortKey(b))),
    [tasks]
  );

  const isSaving = isCreatingTask || isUpdatingTask;
  const canCreate = registry.length > 0;

  const openCreateSheet = () => {
    setSubmitError(null);
    setSheet({ open: true, mode: 'create', task: null });
  };

  const openEditSheet = (task: ScheduledTask) => {
    setSubmitError(null);
    setSheet({ open: true, mode: 'edit', task });
  };

  const closeSheet = () => setSheet({ open: false });

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) {
      closeSheet();
    }
  };

  const handleCreate = async(values: SchedulerTaskFormValues) => {
    const created = await createTask(values).unwrap();
    setSubmitError(null);
    setSheet({ open: true, mode: 'edit', task: created });
  };

  const handleUpdate = async(values: SchedulerTaskFormValues) => {
    if (!sheet.open || sheet.mode !== 'edit' || !sheet.task) {
      return;
    }
    const updated = await updateTask({ id: sheet.task.id, body: values }).unwrap();
    setSubmitError(null);
    setSheet({ open: true, mode: 'edit', task: updated });
  };

  const handleSheetSubmit = async(values: SchedulerTaskFormValues) => {
    try {
      if (sheet.open && sheet.mode === 'create') {
        await handleCreate(values);
        return;
      }
      await handleUpdate(values);
    } catch (error) {
      setSubmitError(formatSchedulerMutationError(error));
    }
  };

  return (
    <div className="space-y-6 overflow-y-auto">
      {failures?.has_failures ? (
        <div
          className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700"
          role="alert"
        >
          <p className="font-medium">
            {failures.failure_count}
            {' '}
            scheduled task
            {failures.failure_count === 1 ? '' : 's'}
            {' '}
            failed in
            the last few hours.
          </p>
          <ul className="mt-2 list-disc pl-5">
            {failures.tasks.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  className="text-left underline-offset-2 hover:underline"
                  onClick={() => openEditSheet(task)}
                >
                  {task.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {activeTasks && activeTasks.length > 0 ? (
        <section className="rounded-md border border-gray-400 bg-white p-4">
          <p className="text-sm text-gray-500">
            Recently active
          </p>
          <ul className="mt-3 space-y-2">
            {activeTasks.map((task) => (
              <li
                key={task.id}
                className="flex flex-wrap items-center gap-2 text-sm text-black"
              >
                <span className="font-medium">
                  {task.name}
                </span>
                <span className="text-gray-500">
                  Last run
                  {' '}
                  {task.last_run_at ? formatDisplayDateTime(task.last_run_at) : '—'}
                </span>
                <SchedulerStatusBadge status={task.last_status} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="rounded-md border border-gray-400 bg-white p-4 sm:p-5">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-medium text-black">
              Scheduled tasks
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {registry.length
                ? `${registry.length} registered task${registry.length === 1 ? '' : 's'} available to schedule. Saved schedules appear below.`
                : 'No registered tasks are available yet. Saved schedules appear below once tasks are registered.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="med"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              <RefreshCw className={isFetching ? 'animate-spin' : undefined} />
              Refresh
            </Button>
            <Button
              type="button"
              variant="black"
              size="med"
              disabled={!canCreate}
              onClick={openCreateSheet}
            >
              New task
            </Button>
          </div>
        </div>

        <SchedulerTaskList
          tasks={sortedTasks}
          isLoading={isLoading}
          isError={isError}
          onSelect={openEditSheet}
        />
      </section>

      {sheet.open ? (
        <SchedulerTaskSheet
          open={sheet.open}
          mode={sheet.mode}
          task={sheet.task}
          registry={registry}
          isSaving={isSaving}
          submitError={submitError}
          onOpenChange={handleSheetOpenChange}
          onSubmit={handleSheetSubmit}
        />
      ) : null}
    </div>
  );
};

export default SchedulerDashboardTab;
